"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
// biome-ignore lint/style/useImportType: required for JSX runtime compatibility with older toolchains
const react_1 = tslib_1.__importStar(require("react"));
const react_dom_1 = tslib_1.__importDefault(require("react-dom"));
const react_popper_1 = require("react-popper");
const Placement_1 = require("../../values/Placement");
const Trigger_1 = require("../../values/Trigger");
const mergeRefs_1 = tslib_1.__importDefault(require("../../utils/mergeRefs"));
const useClickOutside_1 = require("../../hooks/useClickOutside");
const useTimeout_1 = require("../../hooks/useTimeout");
const useUncontrollable_1 = require("../../hooks/useUncontrollable");
const OverlayTrigger = (props) => {
    const { show: propsShow, defaultShow = false, enablePortal = true, trigger = Trigger_1.TRIGGER.HOVER, delay, overlay, placement = 'top', popperConfig, onToggle = () => { }, rootClose = true, children, } = props;
    // Use a hook to handle controlled props that work in pairs like in this case the "show"  and "onToggle"
    // props. It returns a setter function that automatically triggers the callback.
    const [show, setShow] = (0, useUncontrollable_1.useUncontrolledProp)(propsShow, defaultShow, onToggle);
    const hoverStateRef = (0, react_1.useRef)('');
    const timeout = (0, useTimeout_1.useChainedTimeout)();
    const { onFocus, onBlur, onClick } = typeof children !== 'function' ? react_1.default.Children.only(children).props : {};
    // Simple implementation of mouseEnter and mouseLeave.
    // React's built version is broken: https://github.com/facebook/react/issues/4251
    // for cases when the trigger is disabled and mouseOut/Over can cause flicker
    // moving from one child element to another.
    const handleMouseOverOut = (handler, args, relatedNative) => {
        const [event] = args;
        const target = event.currentTarget;
        const related = event.relatedTarget;
        if ((!related || related !== target) && !target.contains(related)) {
            handler(...args);
        }
    };
    const handleShow = () => {
        timeout.clear();
        hoverStateRef.current = 'show';
        if (typeof delay === 'number' || !delay?.show) {
            setShow(true);
            return;
        }
        timeout.set(() => {
            if (hoverStateRef.current === 'show') {
                setShow(true);
            }
        }, typeof delay === 'number' ? delay : delay?.show);
    };
    const handleHide = () => {
        timeout.clear();
        hoverStateRef.current = 'hide';
        if (typeof delay === 'number' || !delay?.hide) {
            setShow(false);
            return;
        }
        timeout.set(() => {
            if (hoverStateRef.current === 'hide') {
                setShow(false);
            }
        }, typeof delay === 'number' ? delay : delay?.hide);
    };
    const handleFocus = (0, react_1.useCallback)((...args) => {
        handleShow();
        onFocus?.(...args);
    }, [handleShow, onFocus]);
    const handleBlur = (0, react_1.useCallback)((...args) => {
        handleHide();
        onBlur?.(...args);
    }, [handleHide, onBlur]);
    const handleClick = (0, react_1.useCallback)((...args) => {
        setShow(!show);
        onClick?.(...args);
    }, [onClick, setShow, show]);
    const handleMouseOver = (0, react_1.useCallback)((...args) => {
        handleMouseOverOut(handleShow, args, 'fromElement');
    }, [handleShow]);
    const handleMouseOut = (0, react_1.useCallback)((...args) => {
        handleMouseOverOut(handleHide, args, 'toElement');
    }, [handleHide]);
    const [triggerNode, setTriggerNode] = (0, react_1.useState)(null);
    const [popperElement, setPopperElement] = (0, react_1.useState)(null);
    const [arrowElement, setArrowElement] = (0, react_1.useState)(null);
    const [arrowPlacement, setArrowPlacement] = (0, react_1.useState)(placement);
    const handleClickOutside = (0, react_1.useCallback)((event) => {
        const isToggleTarget = triggerNode?.contains(event.target);
        const isOverlayTarget = popperElement?.contains(event.target);
        // Check if the click occurred outside the trigger element. Clicking on the trigger
        // itself is handled by the onClick handler
        if (show && rootClose && !isToggleTarget && !isOverlayTarget) {
            handleHide();
        }
    }, [triggerNode, popperElement, rootClose, handleHide]);
    (0, useClickOutside_1.useClickOutsideWithRef)(triggerNode, handleClickOutside);
    const triggerProps = {
        ref: (0, mergeRefs_1.default)([children.ref, setTriggerNode]),
    };
    if (trigger === 'click') {
        triggerProps.onClick = handleClick;
    }
    else if (trigger === 'focus') {
        triggerProps.onFocus = handleFocus;
        triggerProps.onBlur = handleBlur;
    }
    else if (trigger === 'hover') {
        triggerProps.onMouseOver = handleMouseOver;
        triggerProps.onMouseOut = handleMouseOut;
    }
    const defaultPopperConfig = {
        placement,
        modifiers: [
            {
                name: 'arrow',
                options: {
                    element: arrowElement,
                },
            },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['right', 'left', 'top'],
                },
            },
        ],
    };
    if (popperConfig) {
        popperConfig.placement = placement;
        // if it has an arrow modifier, inject the arrow element
        const updatedModifiers = [];
        popperConfig.modifiers?.forEach((mod) => {
            if (mod.name !== 'arrow') {
                return updatedModifiers.push(mod);
            }
            return updatedModifiers.push({
                ...mod,
                options: {
                    ...mod.options,
                    element: arrowElement,
                },
            });
        });
        popperConfig.modifiers = updatedModifiers;
    }
    const popper = (0, react_popper_1.usePopper)(triggerNode, popperElement, popperConfig || defaultPopperConfig);
    // In case the overlay causes an overflow and the "flip" modifier
    // changes the overlays placement, we nee to update the arrow placement as well
    (0, react_1.useEffect)(() => {
        if (popper.state) {
            // Adjust arrow styles based on placement if necessary
            setArrowPlacement(popper.state.placement);
        }
    }, [popper.state?.placement]);
    const overlayElement = react_1.default.cloneElement(overlay, {
        ...popper.attributes.popper,
        ref: setPopperElement,
        placement: arrowPlacement,
        style: { ...popper.styles.popper },
        arrowProps: {
            ...popper.attributes.arrow,
            style: popper.styles.arrow,
            ref: setArrowElement,
        },
    });
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [show && (enablePortal ? react_dom_1.default.createPortal(overlayElement, document.body) : overlayElement), react_1.default.cloneElement(children, triggerProps)] }));
};
OverlayTrigger.TRIGGER_CLICK = Trigger_1.TRIGGER.CLICK;
OverlayTrigger.TRIGGER_HOVER = Trigger_1.TRIGGER.HOVER;
OverlayTrigger.TRIGGER_FOCUS = Trigger_1.TRIGGER.FOCUS;
// placement
OverlayTrigger.AUTO_START = Placement_1.PLACEMENT.AUTO_START;
OverlayTrigger.AUTO = Placement_1.PLACEMENT.AUTO;
OverlayTrigger.AUTO_END = Placement_1.PLACEMENT.AUTO_END;
OverlayTrigger.TOP_START = Placement_1.PLACEMENT.TOP_START;
OverlayTrigger.TOP = Placement_1.PLACEMENT.TOP;
OverlayTrigger.TOP_END = Placement_1.PLACEMENT.TOP_END;
OverlayTrigger.RIGHT_START = Placement_1.PLACEMENT.RIGHT_START;
OverlayTrigger.RIGHT = Placement_1.PLACEMENT.RIGHT;
OverlayTrigger.RIGHT_END = Placement_1.PLACEMENT.RIGHT_END;
OverlayTrigger.BOTTOM_START = Placement_1.PLACEMENT.BOTTOM_START;
OverlayTrigger.BOTTOM = Placement_1.PLACEMENT.BOTTOM;
OverlayTrigger.BOTTOM_END = Placement_1.PLACEMENT.BOTTOM_END;
OverlayTrigger.LEFT_START = Placement_1.PLACEMENT.LEFT_START;
OverlayTrigger.LEFT = Placement_1.PLACEMENT.LEFT;
OverlayTrigger.LEFT_END = Placement_1.PLACEMENT.LEFT_END;
exports.default = OverlayTrigger;
